import { Splide } from '@splidejs/splide';
import '@splidejs/splide/css';
import { getPerformanceObserverHelper } from '@helpers/performance-observer-helper.js';

const SESSION_MUTE_KEY = 'video-shorts-muted';
const REBUILD_DELAY = 180;
const DEBUG_PREFIX = '[video-shorts-carousel]';
const DEBUG_STORAGE_KEY = 'video-shorts-carousel-debug';

class Carousel extends HTMLElement {
  constructor() {
    super();

    this.isInitialized = false;
    // Start as false - IntersectionObserver will set the real value before first play
    this.isInViewport = false;
    this.isMuted = true;
    this.isPlaybackPausedByUser = false;
    this.pendingPlayIndex = null;
    this.pendingFullscreenIndex = null;
    this.activeFullscreenVideo = null;
    this.currentIndex = 0;
    this.sectionIndex = null;
    this.isFirstSection = false;
    this.isLoopMode = false;
    this.displayedSlidesCount = 0;
    this.isInternalMutation = false;
    this.rebuildTimer = null;
    this.copySuccessState = new WeakMap();
    this.copySuccessTimeouts = new Set();
    this.performanceHelper = getPerformanceObserverHelper();
    this.sectionPerformance = null;

    this.onSlideMove = this.onSlideMove.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onWindowLoad = this.onWindowLoad.bind(this);
    this.onHashChange = this.onHashChange.bind(this);
    this.onFullscreenChange = this.onFullscreenChange.bind(this);
    this.onVideoBeginFullscreen = this.onVideoBeginFullscreen.bind(this);
    this.onVideoEndFullscreen = this.onVideoEndFullscreen.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) return;

    this.log('connectedCallback start');

    this.splideElement = this.querySelector('[data-carousel-splide]');

    if (!this.splideElement) return;

    this.listElement = this.splideElement.querySelector('.splide__list');

    if (!this.listElement) return;

    this.copySuccessMessage = this.dataset.copySuccessMessage || '';
    this.copyPromptLabel = this.dataset.copyPromptLabel || 'Copy link';

    this.isMuted = this.getStoredMuteState();
    this.resolveSectionMode();
    this.performanceHelper.start();
    this.sectionPerformance = this.performanceHelper.trackSection(this, {
      name: 'video-shorts-carousel',
    });

    this.syncSlidesAndCarousel({ forceRemount: true });
    this.setupViewportObserver();
    this.setupSlidesObserver();

    // Play immediately if section is already visible - IntersectionObserver fires
    // asynchronously so we trigger the first play synchronously here.
    if (this.isInViewport) {
      this.playActiveVideo();
    }

    this.log('connectedCallback done', {
      displayedSlidesCount: this.displayedSlidesCount,
    });

    this.sectionPerformance?.endMount();

    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.log('disconnectedCallback start');

    if (this.rebuildTimer) {
      window.clearTimeout(this.rebuildTimer);
      this.rebuildTimer = null;
    }

    if (this.slidesObserver) {
      this.slidesObserver.disconnect();
    }

    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
    }

    this.copySuccessTimeouts.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    this.copySuccessTimeouts.clear();

    this.clearBoundListeners();
    this.restoreFullscreenVideoStyles();

    if (this.splide) {
      this.splide.destroy(true);
    }

    this.performanceHelper.flush('carousel-disconnect');

    this.isInitialized = false;

    this.log('disconnectedCallback done');
  }

  setupInitialHashSync() {
    this.syncFromHash({ smooth: false });

    window.addEventListener('load', this.onWindowLoad, {
      signal: this.eventsController.signal,
    });

    window.addEventListener('hashchange', this.onHashChange, {
      signal: this.eventsController.signal,
    });
  }

  onWindowLoad() {
    this.syncFromHash({ smooth: false });
  }

  onHashChange() {
    this.syncFromHash({ smooth: true });
  }

  syncFromHash({ smooth = false } = {}) {
    const currentHash = decodeURIComponent(window.location.hash || '');

    if (!currentHash.startsWith('#Slide-')) return;

    const targetId = currentHash.slice(1);
    const targetIndex = this.findSlideIndexByAnchorId(targetId);

    if (targetIndex === -1) return;

    this.currentIndex = targetIndex;

    if (this.splide) {
      this.splide.go(targetIndex);
    }

    this.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block: 'start',
    });

    this.log('syncFromHash', {
      targetId,
      targetIndex,
    });
  }

  syncSlidesAndCarousel({ forceRemount = false } = {}) {
    const previousSlidesCount = this.displayedSlidesCount;

    this.collectElements();

    if (!this.slides.length) return;

    const shouldRemount = forceRemount || !this.splide || previousSlidesCount !== this.slides.length;

    this.log('syncSlidesAndCarousel', {
      forceRemount,
      shouldRemount,
      previousSlidesCount,
      currentSlidesCount: this.slides.length,
    });

    if (shouldRemount) {
      this.setupSplide();
    } else {
      this.refreshSplide();
    }

    this.displayedSlidesCount = this.slides.length;

    this.clearBoundListeners();
    this.setupFullscreenStateObservers();
    this.setupNavigationControls();
    this.setupSlideInteractions();
    this.setupShareActions();
    this.setupMuteActions();
    this.setupPlayActions();
    this.setupFullscreenActions();
    this.setupInitialHashSync();
    this.setupVideoFallbackHandlers();

    const maxIndex = Math.max(this.displayedSlidesCount - 1, 0);

    this.currentIndex = Math.min(this.currentIndex, maxIndex);
    this.syncActiveSlide(this.currentIndex);

    this.log('syncSlidesAndCarousel done', {
      displayedSlidesCount: this.displayedSlidesCount,
      currentIndex: this.currentIndex,
    });
  }

  resolveSectionMode() {
    const parsedSectionIndex = Number.parseInt(this.dataset.sectionIndex || '', 10);

    this.sectionIndex = Number.isNaN(parsedSectionIndex) ? null : parsedSectionIndex;
    this.isFirstSection = this.sectionIndex === 1;

    if (this.isFirstSection) {
      // First section should start in eager mode without viewport observer gating.
      this.isInViewport = true;
    }

    this.log('resolveSectionMode', {
      sectionIndex: this.sectionIndex,
      isFirstSection: this.isFirstSection,
    });
  }

  collectElements() {
    this.slides = this.getSourceSlides();
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.shareButtons = Array.from(this.querySelectorAll('[data-carousel-share]'));
    this.muteButtons = Array.from(this.querySelectorAll('[data-carousel-mute-toggle]'));
    this.playButtons = Array.from(this.querySelectorAll('[data-carousel-play-toggle]'));
    this.fullscreenButtons = Array.from(this.querySelectorAll('[data-carousel-fullscreen-toggle]'));
  }

  setupSlidesObserver() {
    if (this.isFirstSection) return;

    this.slidesObserver = new MutationObserver((mutations) => {
      if (this.isInternalMutation) return;

      const hasRealSlideChanges = mutations.some((mutation) => {
        const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];

        return changedNodes.some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;

          if (!node.classList?.contains('splide__slide')) return false;

          if (node.classList.contains('splide__slide--clone')) return false;

          return true;
        });
      });

      if (!hasRealSlideChanges) return;

      this.log('MutationObserver detected real slide changes, scheduling rebuild');

      this.scheduleCarouselRebuild();
    });

    this.slidesObserver.observe(this.listElement, {
      childList: true,
    });
  }

  scheduleCarouselRebuild() {
    if (this.rebuildTimer) {
      window.clearTimeout(this.rebuildTimer);
    }

    this.rebuildTimer = window.setTimeout(() => {
      this.rebuildTimer = null;
      this.log('debounced rebuild fired');
      this.syncSlidesAndCarousel({ forceRemount: true });
    }, REBUILD_DELAY);
  }

  getSourceSlides() {
    return Array.from(this.listElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
  }

  getSlideAnchorId(slideElement) {
    if (!slideElement) return '';

    const slideAnchorElement = slideElement.querySelector('[id^="Slide-"]');

    if (!slideAnchorElement) return '';

    return slideAnchorElement.id || '';
  }

  findSlideIndexByAnchorId(anchorId) {
    if (!anchorId) return -1;

    return this.slides.findIndex((slide) => this.getSlideAnchorId(slide) === anchorId);
  }

  resolveSlideIndex(slideElement) {
    if (!slideElement || !this.displayedSlidesCount) return -1;

    const directIndex = this.slides.indexOf(slideElement);

    if (directIndex !== -1) return directIndex;

    const splideIndex = Number.parseInt(slideElement.getAttribute('data-splide-index') || '', 10);

    if (!Number.isNaN(splideIndex)) {
      return ((splideIndex % this.displayedSlidesCount) + this.displayedSlidesCount) % this.displayedSlidesCount;
    }

    const slideId = slideElement.id;

    if (slideId) {
      const idMatchIndex = this.slides.findIndex((slide) => slide.id === slideId);

      if (idMatchIndex !== -1) return idMatchIndex;
    }

    const ariaLabel = slideElement.getAttribute('aria-label') || '';
    const labelMatch = ariaLabel.match(/^(\d+)/);

    if (labelMatch) {
      const ariaIndex = Number.parseInt(labelMatch[1], 10) - 1;

      if (ariaIndex >= 0 && ariaIndex < this.displayedSlidesCount) {
        return ariaIndex;
      }
    }

    return -1;
  }

  normalizeSlideIndex(index) {
    if (!Number.isInteger(index) || !this.displayedSlidesCount) return -1;

    return ((index % this.displayedSlidesCount) + this.displayedSlidesCount) % this.displayedSlidesCount;
  }

  resolveSlideTarget(slideElement) {
    if (!slideElement) {
      return {
        goIndex: -1,
        logicalIndex: -1,
      };
    }

    const splideSlides = this.splide?.Components?.Slides?.get?.() ?? [];
    const matchedSplideSlide = splideSlides.find((entry) => entry.slide === slideElement);

    if (matchedSplideSlide) {
      const sourceIndex = matchedSplideSlide.slideIndex;
      const logicalSourceIndex = sourceIndex >= 0 ? sourceIndex : matchedSplideSlide.index;

      return {
        goIndex: matchedSplideSlide.index,
        logicalIndex: this.normalizeSlideIndex(logicalSourceIndex),
      };
    }

    const fallbackLogicalIndex = this.resolveSlideIndex(slideElement);

    return {
      goIndex: fallbackLogicalIndex,
      logicalIndex: fallbackLogicalIndex,
    };
  }

  clearBoundListeners() {
    if (this.eventsController) {
      this.eventsController.abort();
    }

    this.eventsController = new AbortController();
  }

  setupSplide() {
    this.isInternalMutation = true;

    if (this.splide) {
      this.splide.destroy(true);
    }

    const canUseLoopMode = this.slides.length > 1;

    this.isLoopMode = canUseLoopMode;

    this.log('setupSplide', {
      canUseLoopMode,
      slidesCount: this.slides.length,
    });

    this.splide = new Splide(this.splideElement, {
      type: canUseLoopMode ? 'loop' : 'slide',
      speed: 800,
      focus: 'center',
      pagination: false,
      arrows: false,
      drag: true,
      keyboard: 'focused',
      rewind: false,
      trimSpace: false,
      omitEnd: false,
      autoWidth: true,
      gap: '1rem',
      mediaQuery: 'min',
    });

    this.splide.on('mounted', () => {
      this.scheduleCloneSanitization();
      this.currentIndex = this.splide.index;
      this.syncFromHash({ smooth: false });
      this.syncActiveSlide(this.currentIndex);
    });

    this.splide.on('move', this.onSlideMove);

    this.splide.mount();

    window.setTimeout(() => {
      this.isInternalMutation = false;
    }, 0);
  }

  sanitizeCloneSlideIds() {
    const cloneSlides = this.listElement?.querySelectorAll('.splide__slide--clone') ?? [];

    cloneSlides.forEach((slide) => {
      const sourceSlideId = slide.getAttribute('id');

      if (sourceSlideId) {
        slide.setAttribute('data-source-slide-id', sourceSlideId);
        slide.removeAttribute('id');
      }

      const cloneAnchorElements = slide.querySelectorAll('[id^="Slide-"]');

      cloneAnchorElements.forEach((anchorElement) => {
        const sourceAnchorId = anchorElement.getAttribute('id');

        if (!sourceAnchorId) return;

        anchorElement.setAttribute('data-source-anchor-id', sourceAnchorId);
        anchorElement.removeAttribute('id');
      });
    });
  }

  scheduleCloneSanitization() {
    this.sanitizeCloneSlideIds();

    window.requestAnimationFrame(() => {
      this.sanitizeCloneSlideIds();
    });

    window.setTimeout(() => {
      this.sanitizeCloneSlideIds();
    }, 0);
  }

  setupNavigationControls() {
    const canNavigate = this.displayedSlidesCount > 1;

    this.toggleNavigationButtons(canNavigate);

    if (!canNavigate) return;

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.splide.go('<'), {
        signal: this.eventsController.signal,
      });

      this.prevButton.addEventListener(
        'click',
        () => {
          const token = this.performanceHelper.trackBlockStart(this.prevButton, 'arrow_prev_click');

          window.requestAnimationFrame(() => {
            this.performanceHelper.trackBlockEnd(token, {
              source: 'carousel-prev',
            });
          });
        },
        { signal: this.eventsController.signal }
      );
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.splide.go('>'), {
        signal: this.eventsController.signal,
      });

      this.nextButton.addEventListener(
        'click',
        () => {
          const token = this.performanceHelper.trackBlockStart(this.nextButton, 'arrow_next_click');

          window.requestAnimationFrame(() => {
            this.performanceHelper.trackBlockEnd(token, {
              source: 'carousel-next',
            });
          });
        },
        { signal: this.eventsController.signal }
      );
    }
  }

  toggleNavigationButtons(canNavigate) {
    [this.prevButton, this.nextButton].forEach((button) => {
      if (!button) return;

      button.hidden = !canNavigate;
      button.disabled = !canNavigate;
    });
  }

  refreshSplide() {
    if (!this.splide) return;

    this.log('refreshSplide');

    this.isInternalMutation = true;
    this.splide.refresh();
    this.scheduleCloneSanitization();

    window.setTimeout(() => {
      this.isInternalMutation = false;
    }, 0);
  }

  setupSlideInteractions() {
    this.slides.forEach((slide, index) => {
      slide.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('a, button, input, select, textarea, [role="button"]')) return;

          this.splide.go(index);
        },
        { signal: this.eventsController.signal }
      );
    });
  }

  setupShareActions() {
    this.shareButtons.forEach((button) => {
      button.addEventListener(
        'click',
        async (event) => {
          const token = this.performanceHelper.trackBlockStart(button, 'share_click');

          event.preventDefault();
          event.stopPropagation();

          const { shareUrl, shareTitle } = button.dataset;

          if (!shareUrl) {
            this.performanceHelper.trackBlockEnd(token, {
              source: 'missing-share-url',
            });

            return;
          }

          if (navigator.share) {
            try {
              await navigator.share({
                title: shareTitle,
                url: shareUrl,
              });

              this.performanceHelper.trackBlockEnd(token, {
                source: 'navigator-share',
              });

              return;
            } catch (error) {
              if (error?.name === 'AbortError') {
                this.performanceHelper.trackBlockEnd(token, {
                  source: 'navigator-share-abort',
                });

                return;
              }
            }
          }

          if (navigator.clipboard?.writeText) {
            try {
              await navigator.clipboard.writeText(shareUrl);
              this.showCopySuccess(button);
              this.performanceHelper.trackBlockEnd(token, {
                source: 'clipboard',
              });

              return;
            } catch (_error) {
              this.performanceHelper.trackBlockEnd(token, {
                source: 'prompt-fallback',
              });
              window.prompt(this.copyPromptLabel, shareUrl);

              return;
            }
          }

          this.performanceHelper.trackBlockEnd(token, {
            source: 'prompt-fallback',
          });

          window.prompt(this.copyPromptLabel, shareUrl);
        },
        { signal: this.eventsController.signal }
      );
    });
  }

  setupMuteActions() {
    this.applyMuteStateToAllVideos(this.isMuted);
    this.updateMuteButtons();

    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-carousel-mute-toggle]');

        if (!button || !this.contains(button)) return;

        const token = this.performanceHelper.trackBlockStart(button, 'mute_toggle');

        event.preventDefault();
        event.stopPropagation();

        this.isMuted = !this.isMuted;
        this.setStoredMuteState(this.isMuted);

        this.applyMuteStateToAllVideos(this.isMuted);
        this.updateMuteButtons();

        if (!this.isMuted && !this.isPlaybackPausedByUser) {
          this.playActiveVideo();
        }

        this.performanceHelper.trackBlockEnd(token, {
          isMuted: this.isMuted,
        });
      },
      { signal: this.eventsController.signal }
    );
  }

  setupPlayActions() {
    this.updatePlayButtons();

    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-carousel-play-toggle]');

        if (!button || !this.contains(button)) return;

        const token = this.performanceHelper.trackBlockStart(button, 'play_toggle');

        event.preventDefault();
        event.stopPropagation();

        const buttonSlide = button.closest('.splide__slide');
        const { goIndex: goTargetIndex, logicalIndex: buttonSlideIndex } = this.resolveSlideTarget(buttonSlide);
        const isDifferentSlide = buttonSlideIndex !== -1 && buttonSlideIndex !== this.currentIndex;

        if (isDifferentSlide) {
          this.isPlaybackPausedByUser = false;

          if (this.splide) {
            this.pendingPlayIndex = buttonSlideIndex;
            this.splide.go(goTargetIndex);
          } else {
            this.currentIndex = buttonSlideIndex;

            if (this.isInViewport) {
              this.playActiveVideo();
            } else {
              this.updatePlayButtons();
            }
          }

          this.performanceHelper.trackBlockEnd(token, {
            switchedToIndex: buttonSlideIndex,
            goTargetIndex,
          });

          return;
        }

        const activeSlide = this.slides[this.currentIndex];
        const video = activeSlide?.querySelector('video');

        if (!video) {
          this.performanceHelper.trackBlockEnd(token, {
            state: 'no-video',
          });

          return;
        }

        if (!video.paused && !video.ended) {
          this.isPlaybackPausedByUser = true;
          this.pauseAllVideos();
        } else {
          this.isPlaybackPausedByUser = false;

          if (this.isInViewport) {
            this.playActiveVideo();
          }
        }

        this.updatePlayButtons();

        this.performanceHelper.trackBlockEnd(token, {
          pausedByUser: this.isPlaybackPausedByUser,
        });
      },
      { signal: this.eventsController.signal }
    );
  }

  setupFullscreenStateObservers() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange, {
      signal: this.eventsController.signal,
    });

    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange, {
      signal: this.eventsController.signal,
    });

    this.getAllVideos().forEach((video) => {
      video.addEventListener('webkitbeginfullscreen', this.onVideoBeginFullscreen, {
        signal: this.eventsController.signal,
      });

      video.addEventListener('webkitendfullscreen', this.onVideoEndFullscreen, {
        signal: this.eventsController.signal,
      });
    });
  }

  setupFullscreenActions() {
    this.addEventListener(
      'click',
      async (event) => {
        const button = event.target.closest('[data-carousel-fullscreen-toggle]');

        if (!button || !this.contains(button)) return;

        const token = this.performanceHelper.trackBlockStart(button, 'fullscreen_toggle');

        event.preventDefault();
        event.stopPropagation();

        if (!this.isMobileOrTabletViewport()) {
          this.performanceHelper.trackBlockEnd(token, {
            source: 'desktop-disabled',
          });

          return;
        }

        const buttonSlide = button.closest('.splide__slide');
        const { goIndex: goTargetIndex, logicalIndex: buttonSlideIndex } = this.resolveSlideTarget(buttonSlide);
        const isDifferentSlide = buttonSlideIndex !== -1 && buttonSlideIndex !== this.currentIndex;

        if (isDifferentSlide && this.splide) {
          this.isPlaybackPausedByUser = false;
          this.pendingPlayIndex = buttonSlideIndex;
          this.pendingFullscreenIndex = buttonSlideIndex;
          this.splide.go(goTargetIndex);

          this.performanceHelper.trackBlockEnd(token, {
            source: 'pending-slide-change',
            switchedToIndex: buttonSlideIndex,
            goTargetIndex,
          });

          return;
        }

        this.isPlaybackPausedByUser = false;

        if (this.isInViewport) {
          this.playActiveVideo();
        }

        const source = await this.enterFullscreenForSlide(
          buttonSlideIndex === -1 ? this.slides[this.currentIndex] : this.slides[buttonSlideIndex]
        );

        this.performanceHelper.trackBlockEnd(token, {
          source,
          index: buttonSlideIndex === -1 ? this.currentIndex : buttonSlideIndex,
        });
      },
      { signal: this.eventsController.signal }
    );
  }

  setupVideoFallbackHandlers() {
    this.getAllVideos().forEach((video) => {
      video.addEventListener('error', () => {
        const poster = video.getAttribute('poster');

        if (!poster || video.nextElementSibling?.matches('[data-video-poster-fallback]')) return;

        video.classList.add('opacity-0');

        const posterImage = document.createElement('img');

        posterImage.src = poster;
        posterImage.alt = '';
        posterImage.loading = this.isFirstSection ? 'eager' : 'lazy';
        posterImage.dataset.videoPosterFallback = 'true';
        posterImage.className = 'absolute inset-0 h-full w-full object-cover';

        video.parentElement?.appendChild(posterImage);
      });
    });
  }

  setupViewportObserver() {
    if (this.isFirstSection) return;

    // Synchronously determine initial visibility so that playActiveVideo() can
    // be triggered before the async IntersectionObserver fires its first entry.
    const rect = this.getBoundingClientRect();

    this.isInViewport = rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver(this.onVisibilityChange, {
      root: null,
      threshold: 0.3,
    });

    this.viewportObserver.observe(this);
  }

  onSlideMove(newIndex) {
    const normalizedNewIndex = this.normalizeSlideIndex(newIndex);
    const shouldPlayAfterMove = this.pendingPlayIndex !== null && this.pendingPlayIndex === normalizedNewIndex;

    const shouldEnterFullscreenAfterMove =
      this.pendingFullscreenIndex !== null && this.pendingFullscreenIndex === normalizedNewIndex;

    this.pendingPlayIndex = null;
    this.pendingFullscreenIndex = null;

    if (normalizedNewIndex !== this.currentIndex) {
      this.isPlaybackPausedByUser = false;
    }

    this.currentIndex = normalizedNewIndex;
    this.log('onSlideMove', { newIndex, normalizedNewIndex, shouldPlayAfterMove });

    if (shouldPlayAfterMove && this.isInViewport) {
      this.playActiveVideo();

      if (shouldEnterFullscreenAfterMove) {
        window.requestAnimationFrame(() => {
          void this.enterFullscreenForCurrentSlide();
        });
      }

      return;
    }

    this.syncActiveSlide(normalizedNewIndex);

    if (shouldEnterFullscreenAfterMove) {
      window.requestAnimationFrame(() => {
        void this.enterFullscreenForCurrentSlide();
      });
    }
  }

  onVisibilityChange(entries) {
    entries.forEach((entry) => {
      this.isInViewport = entry.isIntersecting;

      this.log('onVisibilityChange', {
        isInViewport: this.isInViewport,
      });

      if (!this.isInViewport) {
        this.pauseAllVideos();
      } else {
        if (this.isPlaybackPausedByUser) {
          this.updatePlayButtons();
        } else {
          this.playActiveVideo();
        }
      }
    });
  }

  onFullscreenChange() {
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;

    if (!fullscreenElement) {
      this.restoreFullscreenVideoStyles();
    }
  }

  onVideoBeginFullscreen(event) {
    this.applyFullscreenVideoStyles(event.currentTarget);
  }

  onVideoEndFullscreen() {
    this.restoreFullscreenVideoStyles();
  }

  syncActiveSlide(activeIndex) {
    this.preloadActiveAndNextVideos(activeIndex);

    if (this.isInViewport && !this.isPlaybackPausedByUser) {
      this.playActiveVideo();
    } else {
      this.pauseAllVideos();
    }
  }

  preloadActiveAndNextVideos(activeIndex) {
    const shouldWrapNeighborIndexes = this.isLoopMode && this.slides.length > 1;

    const prevIndex = shouldWrapNeighborIndexes
      ? (activeIndex - 1 + this.slides.length) % this.slides.length
      : activeIndex - 1;

    const nextIndex = shouldWrapNeighborIndexes ? (activeIndex + 1) % this.slides.length : activeIndex + 1;
    const activeVideoPreload = this.isFirstSection ? 'auto' : 'metadata';

    this.slides.forEach((slide, index) => {
      const video = slide.querySelector('video');

      if (!video) return;

      if (index === activeIndex) {
        if (video.preload !== activeVideoPreload) {
          video.preload = activeVideoPreload;
        }

        return;
      }

      if (index === nextIndex || index === prevIndex) {
        if (video.preload !== 'metadata') {
          video.preload = 'metadata';
        }

        return;
      }

      if (video.preload !== 'none') {
        video.preload = 'none';
      }
    });
  }

  pauseAllVideos() {
    this.getAllVideos().forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });

    this.updatePlayButtons();
  }

  playActiveVideo() {
    this.pauseAllVideos();

    const activeSlide = this.slides[this.currentIndex];

    if (!activeSlide) return;

    const video = activeSlide.querySelector('video');

    if (!video) return;

    video.muted = this.isMuted;

    const playResult = video.play();

    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch((error) => {
        this.log('playActiveVideo play failed', {
          error,
        });
      });
    }

    this.updatePlayButtons();
  }

  isMobileOrTabletViewport() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia('(max-width: 1023px)').matches;
  }

  async enterFullscreenForCurrentSlide() {
    return this.enterFullscreenForSlide(this.slides[this.currentIndex]);
  }

  async enterFullscreenForSlide(slide) {
    const video = slide?.querySelector('video');

    if (!video) return 'no-video';

    this.applyFullscreenVideoStyles(video);
    video.muted = this.isMuted;

    if ((video.paused || video.ended) && !this.isPlaybackPausedByUser) {
      const playResult = video.play();

      if (playResult && typeof playResult.catch === 'function') {
        await playResult.catch((error) => {
          this.log('enterFullscreenForSlide play failed', {
            error,
          });
        });
      }
    }

    if (typeof video.webkitEnterFullscreen === 'function') {
      try {
        video.webkitEnterFullscreen();

        return 'webkit-video';
      } catch (error) {
        this.restoreFullscreenVideoStyles();
        this.log('enterFullscreenForSlide webkitEnterFullscreen failed', {
          error,
        });
      }
    }

    const fullscreenTarget = typeof video.requestFullscreen === 'function' ? video : slide;

    const requestFullscreen =
      fullscreenTarget?.requestFullscreen ||
      fullscreenTarget?.webkitRequestFullscreen ||
      fullscreenTarget?.msRequestFullscreen;

    if (typeof requestFullscreen !== 'function') {
      return 'unsupported';
    }

    try {
      await requestFullscreen.call(fullscreenTarget);

      return fullscreenTarget === video ? 'video' : 'slide';
    } catch (error) {
      this.restoreFullscreenVideoStyles();
      this.log('enterFullscreenForSlide requestFullscreen failed', {
        error,
      });

      return 'request-failed';
    }
  }

  applyFullscreenVideoStyles(video) {
    if (!video) return;

    if (this.activeFullscreenVideo && this.activeFullscreenVideo !== video) {
      this.activeFullscreenVideo.classList.add('object-cover');
    }

    video.classList.remove('object-cover');
    this.activeFullscreenVideo = video;
  }

  restoreFullscreenVideoStyles() {
    if (!this.activeFullscreenVideo) return;

    this.activeFullscreenVideo.classList.add('object-cover');
    this.activeFullscreenVideo = null;
  }

  applyMuteStateToAllVideos(isMuted) {
    this.getAllVideos().forEach((video) => {
      video.muted = isMuted;
    });
  }

  updateMuteButtons() {
    this.muteButtons = Array.from(this.querySelectorAll('[data-carousel-mute-toggle]'));

    this.muteButtons.forEach((button) => {
      const text = this.isMuted ? button.dataset.labelMuted : button.dataset.labelUnmuted;

      button.classList.toggle('mute', this.isMuted);
      button.classList.toggle('unmute', !this.isMuted);

      if (text) {
        button.setAttribute('aria-label', text);
      }
    });

    this.log('updateMuteButtons', {
      isMuted: this.isMuted,
      buttonsCount: this.muteButtons.length,
    });
  }

  updatePlayButtons() {
    this.playButtons = Array.from(this.querySelectorAll('[data-carousel-play-toggle]'));

    this.playButtons.forEach((button) => {
      const buttonSlide = button.closest('.splide__slide');
      const buttonSlideIndex = this.resolveSlideIndex(buttonSlide);
      const activeSlide = this.slides[this.currentIndex];
      const isActiveSlide = buttonSlideIndex !== -1 && buttonSlideIndex === this.currentIndex;
      const activeVideo = isActiveSlide ? activeSlide?.querySelector('video') : null;

      const isPlaying =
        Boolean(activeVideo) &&
        !activeVideo.paused &&
        !activeVideo.ended &&
        !this.isPlaybackPausedByUser &&
        this.isInViewport;

      const text = isPlaying ? button.dataset.labelPause : button.dataset.labelPlay;

      button.classList.toggle('active', isPlaying);
      button.classList.toggle('pause', isPlaying);

      if (text) {
        button.setAttribute('aria-label', text);
      }
    });

    this.log('updatePlayButtons', {
      currentIndex: this.currentIndex,
      pausedByUser: this.isPlaybackPausedByUser,
      buttonsCount: this.playButtons.length,
    });
  }

  showCopySuccess(button) {
    if (!this.copySuccessMessage) return;

    const previousState = this.copySuccessState.get(button) || {};
    const originalHtml = previousState.originalHtml || button.innerHTML;

    if (previousState.timeoutId) {
      window.clearTimeout(previousState.timeoutId);
      this.copySuccessTimeouts.delete(previousState.timeoutId);
    }

    button.textContent = this.copySuccessMessage;

    const timeoutId = window.setTimeout(() => {
      button.innerHTML = originalHtml;
      this.copySuccessState.delete(button);
      this.copySuccessTimeouts.delete(timeoutId);
    }, 2000);

    this.copySuccessTimeouts.add(timeoutId);

    this.copySuccessState.set(button, {
      originalHtml,
      timeoutId,
    });
  }

  getAllVideos() {
    const cloneSlides = Array.from(this.listElement?.querySelectorAll('.splide__slide--clone') ?? []);

    return [...this.slides, ...cloneSlides].map((slide) => slide.querySelector('video')).filter(Boolean);
  }

  getStoredMuteState() {
    try {
      const storedValue = sessionStorage.getItem(SESSION_MUTE_KEY);

      if (storedValue === null) {
        return true;
      }

      return storedValue === 'true';
    } catch (_error) {
      return true;
    }
  }

  setStoredMuteState(isMuted) {
    try {
      sessionStorage.setItem(SESSION_MUTE_KEY, String(isMuted));
    } catch (_error) {
      // no-op
    }
  }

  isDebugEnabled() {
    const attrValue = this.getAttribute('data-debug');

    if (attrValue !== null) {
      const normalized = attrValue.toLowerCase();

      return normalized === '' || normalized === 'true' || normalized === '1';
    }

    try {
      return localStorage.getItem(DEBUG_STORAGE_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  }

  setDebug(enabled) {
    const debugEnabled = Boolean(enabled);

    if (debugEnabled) {
      this.setAttribute('data-debug', 'true');
    } else {
      this.removeAttribute('data-debug');
    }

    try {
      localStorage.setItem(DEBUG_STORAGE_KEY, String(debugEnabled));
    } catch (_error) {
      // no-op
    }

    console.info(DEBUG_PREFIX, 'debug toggled', { enabled: debugEnabled });
  }

  log(message, payload) {
    if (!this.isDebugEnabled()) return;

    if (payload === undefined) {
      console.log(DEBUG_PREFIX, message);

      return;
    }

    console.log(DEBUG_PREFIX, message, payload);
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', Carousel);
}

if (!globalThis.videoShortsCarouselDebug) {
  globalThis.videoShortsCarouselDebug = (enabled = true) => {
    const debugEnabled = Boolean(enabled);

    document.querySelectorAll('carousel-component').forEach((element) => {
      if (typeof element.setDebug === 'function') {
        element.setDebug(debugEnabled);
      }
    });

    return debugEnabled;
  };
}
